function getShortLocalePrefix(locale: string): string {
    if (locale === 'en') {
        return '';
    }

    if (locale === 'zh-TW') {
        return '/zh-tw';
    }

    return `/${locale.split('-')[0].toLowerCase()}`;
}

export const LOCALE_CONFIG = {
    en: {
        label: 'English',
        pathPrefix: getShortLocalePrefix('en'),
        htmlLang: 'en',
        hreflang: 'en',
        ogLocale: 'en_US',
    },
    'zh-CN': {
        label: '简体中文',
        pathPrefix: getShortLocalePrefix('zh-CN'),
        htmlLang: 'zh-CN',
        hreflang: 'zh-CN',
        ogLocale: 'zh_CN',
    },
    'zh-TW': {
        label: '繁體中文',
        pathPrefix: getShortLocalePrefix('zh-TW'),
        htmlLang: 'zh-TW',
        hreflang: 'zh-TW',
        ogLocale: 'zh_TW',
    },
    'de-DE': {
        label: 'Deutsch',
        pathPrefix: getShortLocalePrefix('de-DE'),
        htmlLang: 'de-DE',
        hreflang: 'de-DE',
        ogLocale: 'de_DE',
    },
    'pt-BR': {
        label: 'Português (Brasil)',
        pathPrefix: getShortLocalePrefix('pt-BR'),
        htmlLang: 'pt-BR',
        hreflang: 'pt-BR',
        ogLocale: 'pt_BR',
    },
    'es-ES': {
        label: 'Español',
        pathPrefix: getShortLocalePrefix('es-ES'),
        htmlLang: 'es-ES',
        hreflang: 'es-ES',
        ogLocale: 'es_ES',
    },
    'fr-FR': {
        label: 'Français',
        pathPrefix: getShortLocalePrefix('fr-FR'),
        htmlLang: 'fr-FR',
        hreflang: 'fr-FR',
        ogLocale: 'fr_FR',
    },
    'id-ID': {
        label: 'Bahasa Indonesia',
        pathPrefix: getShortLocalePrefix('id-ID'),
        htmlLang: 'id-ID',
        hreflang: 'id-ID',
        ogLocale: 'id_ID',
    },
    'it-IT': {
        label: 'Italiano',
        pathPrefix: getShortLocalePrefix('it-IT'),
        htmlLang: 'it-IT',
        hreflang: 'it-IT',
        ogLocale: 'it_IT',
    },
    'ja-JP': {
        label: '日本語',
        pathPrefix: getShortLocalePrefix('ja-JP'),
        htmlLang: 'ja-JP',
        hreflang: 'ja-JP',
        ogLocale: 'ja_JP',
    },
    'ko-KR': {
        label: '한국어',
        pathPrefix: getShortLocalePrefix('ko-KR'),
        htmlLang: 'ko-KR',
        hreflang: 'ko-KR',
        ogLocale: 'ko_KR',
    },
    'ru-RU': {
        label: 'Русский',
        pathPrefix: getShortLocalePrefix('ru-RU'),
        htmlLang: 'ru-RU',
        hreflang: 'ru-RU',
        ogLocale: 'ru_RU',
    },
    'ar-SA': {
        label: 'العربية',
        pathPrefix: getShortLocalePrefix('ar-SA'),
        htmlLang: 'ar-SA',
        hreflang: 'ar-SA',
        ogLocale: 'ar_SA',
    },
    'tr-TR': {
        label: 'Türkçe',
        pathPrefix: getShortLocalePrefix('tr-TR'),
        htmlLang: 'tr-TR',
        hreflang: 'tr-TR',
        ogLocale: 'tr_TR',
    },
} as const;

export type EnterHomeLocale = keyof typeof LOCALE_CONFIG;

export interface HomeLocaleSnapshot {
    locale: EnterHomeLocale;
    /** 宿主文案，交给 next-intl。 */
    hostMessages: Record<string, unknown>;
    /** Business Components 的 i18next 文案。 */
    sharedMessages: Record<string, unknown>;
    /**
     * 宿主文案是否同时覆盖进共享 i18n。公开字典沿用这种覆盖；区域命名空间是 ICU 目录，
     * 与 Business Components 同名的键覆盖进 i18next 会让插值与复数失效，只给 next-intl。
     */
    overrideSharedMessages: boolean;
}

export const DEFAULT_LOCALE = 'en' satisfies EnterHomeLocale;

export const LOCALES = Object.keys(LOCALE_CONFIG) as EnterHomeLocale[];

export const LEGACY_LOCALE_PREFIXES = Object.fromEntries(
    LOCALES.filter((locale) => locale !== DEFAULT_LOCALE).map((locale) => [locale, `/${locale}`])
) as Partial<Record<EnterHomeLocale, string>>;

export function isSupportedLocale(value: string): value is EnterHomeLocale {
    return Object.prototype.hasOwnProperty.call(LOCALE_CONFIG, value);
}
